import {
  ref,
  computed,
  onBeforeUnmount,
  type ComputedRef,
  type Ref,
} from "vue";
import { resolveTest } from "@/api/tests";
import {
  startSession,
  getSession,
  autosave as autosaveApi,
  submitSession,
  findInProgressSession,
} from "@/api/sessions";
import { tokenStorage } from "@/api/client";
import { handleError } from "@/services/errorHandler";
import type {
  Answer,
  ExamContent,
  ResolvedTest,
  Session,
  Skill,
  SubmitSessionPayload,
} from "@/types";

export interface UseExamSessionOptions {
  skill: Skill;
  level: string;
  number: string | number;
  fetchExam?: (testId: number) => Promise<ExamContent | null>;
  buildAutosave?: () => Answer[];
  buildSubmit?: () => SubmitSessionPayload;
  hydrate?: (sessionDetail: Session, exam: ExamContent | null) => void;
  autosaveInterval?: number;
}

/**
 * Shared exam-session logic for the listening, reading and writing pages.
 *
 * Design goal: integrate the backend WITHOUT altering the existing exam UI or
 * navigation. The exam page shells already own the answer state (via
 * provide/inject); this composable layers session start/restore/autosave/submit
 * and a server-synced timer on top of that state.
 *
 * When the user is not authenticated, everything degrades gracefully to the
 * original local-only behavior (no network calls), so the UI never breaks.
 */
export function useExamSession(options: UseExamSessionOptions) {
  const {
    skill,
    level,
    number,
    fetchExam,
    buildAutosave = () => [],
    buildSubmit = () => ({ answers: [], writing: [] }),
    hydrate = () => {},
    autosaveInterval = 2000,
  } = options;

  const exam: Ref<ExamContent | null> = ref(null);
  const session: Ref<Session | null> = ref(null);
  const test: Ref<ResolvedTest | null> = ref(null);
  const loading = ref(true);
  const error = ref("");
  const saving = ref(false);
  const submitting = ref(false);
  const timeLeft: Ref<number | null> = ref(null);

  const online: ComputedRef<boolean> = computed(() => !!session.value);
  const isAuthenticated = (): boolean => !!tokenStorage.getAccess();

  let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  let countdownTimer: ReturnType<typeof setInterval> | null = null;
  let submitted = false;

  async function init(): Promise<void> {
    loading.value = true;
    error.value = "";
    try {
      test.value = await resolveTest(level, number);

      if (fetchExam) {
        try {
          exam.value = await fetchExam(test.value.id);
        } catch {
          exam.value = null;
        }
      }

      if (isAuthenticated()) {
        let existing = await findInProgressSession(test.value.id, skill);
        if (!existing) {
          existing = await startSession({ test_id: test.value.id, skill });
        }
        const detail = await getSession(existing.id);
        session.value = detail;
        hydrate(detail, exam.value);
        startCountdown();
      }
    } catch (e) {
      error.value = handleError(e, `useExamSession:${skill}`);
    } finally {
      loading.value = false;
    }
  }

  function scheduleAutosave(): void {
    if (!online.value) return;
    if (autosaveTimer) clearTimeout(autosaveTimer);
    autosaveTimer = setTimeout(runAutosave, autosaveInterval);
  }

  async function runAutosave(): Promise<void> {
    if (!online.value || saving.value || !session.value) return;
    const answers = buildAutosave();
    if (!answers || answers.length === 0) return;
    saving.value = true;
    try {
      await autosaveApi(session.value.id, answers);
    } catch (e) {
      handleError(e, `autosave:${skill}`);
    } finally {
      saving.value = false;
    }
  }

  function startCountdown(): void {
    if (!session.value || !session.value.expires_at) return;
    const expiresAt = new Date(session.value.expires_at).getTime();
    const tick = (): void => {
      const remaining = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
      timeLeft.value = remaining;
      if (remaining <= 0) {
        if (countdownTimer) clearInterval(countdownTimer);
        submit(true);
      }
    };
    tick();
    countdownTimer = setInterval(tick, 1000);
  }

  const formattedTimeLeft = computed((): string | null => {
    if (timeLeft.value == null) return null;
    const m = Math.floor(timeLeft.value / 60);
    const s = timeLeft.value % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  });

  async function submit(auto = false): Promise<Session | null> {
    if (!online.value || submitted || !session.value) return null;
    submitting.value = true;
    try {
      if (autosaveTimer) clearTimeout(autosaveTimer);
      const payload = buildSubmit();
      const result = await submitSession(session.value.id, payload);
      session.value = result;
      submitted = true;
      return result;
    } catch (e) {
      if (!auto) error.value = handleError(e, `submit:${skill}`);
      return null;
    } finally {
      submitting.value = false;
    }
  }

  function cleanup(): void {
    if (autosaveTimer) clearTimeout(autosaveTimer);
    if (countdownTimer) clearInterval(countdownTimer);
  }

  onBeforeUnmount(() => {
    if (online.value && !submitted) runAutosave();
    cleanup();
  });

  return {
    exam,
    session,
    test,
    loading,
    error,
    saving,
    submitting,
    online,
    timeLeft,
    formattedTimeLeft,
    init,
    scheduleAutosave,
    runAutosave,
    submit,
  };
}
